/**
 * HorizontalLayout - Horizontal filmstrip layout for photo collections
 *
 * Displays photos in a single horizontal row with uniform height and scrolling.
 * Photo widths are determined by CSS aspect-ratio to maintain photographer's intent.
 *
 * @module PhotoCollection2/layouts/HorizontalLayout
 */

import React, { useRef, useEffect } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { PhotoItem2 } from '../../PhotoItem2';
import { PhotoContextMenu } from '../../PhotoContextMenu';
import { cn } from '@/lib/utils';
import { Text } from '../../atoms/Text';
import { useAuth } from '@/services/auth';
import type { PhotoCollection2LayoutProps, ReorderContext } from '../PhotoCollection2.types';
import type { PhotoItem2ViewModel } from '../../PhotoItem2/PhotoItem2.types';
import { getVariantUrl } from '@/components/photos/AdaptivePhoto';

/**
 * Default aspect ratio for photos when not determinable from data
 * 3:2 is a common aspect ratio for photographs
 */
const DEFAULT_ASPECT_RATIO = 1.5; // 3:2

/**
 * Props for SortableHorizontalItem wrapper
 */
interface SortableHorizontalItemProps {
  photo: PhotoItem2ViewModel;
  aspectRatio: number;
  itemTemplate: PhotoCollection2LayoutProps['itemTemplate'];
  selectionMode: PhotoCollection2LayoutProps['selectionMode'];
  isSelected: boolean;
  zoom: number;
  onItemClick?: (photoId: string, event?: React.MouseEvent) => void;
  reorderContext?: ReorderContext;
  hasSelection: boolean;
  setRef: (el: HTMLDivElement | null) => void;
  /** Current user's ID for ownership check (Feature 081: photo-versioning) */
  currentUserId?: string;
}

/**
 * Wrapper component that makes the entire photo item draggable in horizontal layout
 */
function SortableHorizontalItem({
  photo,
  aspectRatio,
  itemTemplate,
  selectionMode,
  isSelected,
  zoom,
  onItemClick,
  reorderContext,
  hasSelection,
  setRef,
  currentUserId,
}: SortableHorizontalItemProps) {
  const isDragEnabled = reorderContext?.enabled && !hasSelection;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: photo.photoData.id,
    disabled: !isDragEnabled,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : undefined,
    aspectRatio: aspectRatio.toString(),
    cursor: isDragEnabled ? (isDragging ? 'grabbing' : 'grab') : undefined,
  };

  const dragProps = isDragEnabled
    ? {
        ...attributes,
        ...listeners,
        'aria-roledescription': 'sortable',
        'aria-describedby': 'dnd-instructions',
      }
    : {};

  // Feature 081: photo-versioning - Check if current user owns this photo
  const isOwner = currentUserId ? photo.photoData.userId === currentUserId : false;

  return (
    <div
      ref={(el) => {
        setNodeRef(el);
        setRef(el);
      }}
      {...dragProps}
      className="flex-shrink-0 h-full relative"
      style={style}
    >
      <PhotoContextMenu
        photoId={photo.photoData.id}
        isOwner={isOwner}
      >
        <PhotoItem2
          data={photo}
          template={itemTemplate}
          isSelected={selectionMode !== 'none' && isSelected}
          selectable={selectionMode !== 'none'}
          zoom={zoom}
          onClick={(e?: React.MouseEvent) => onItemClick?.(photo.photoData.id, e)}
        />
      </PhotoContextMenu>
    </div>
  );
}

/**
 * Extract aspect ratio from photo data
 * Priority: 1) explicit aspectRatio property, 2) parse from URL, 3) default
 * URLs from picsum.photos are in format: .../WIDTH/HEIGHT
 *
 * @param photo - Uses flexible typing to support various photo data sources (test data, API responses, mock data)
 */
function getAspectRatio(photo: PhotoItem2ViewModel | any): number {
  // Prefer explicit aspectRatio property if available
  if (photo.aspectRatio && photo.aspectRatio > 0) {
    return photo.aspectRatio;
  }

  // Try to extract from photo URLs (support both PhotoItem2ViewModel and legacy formats)
  const photoObj = photo.photoData || photo;
  // Use getVariantUrl with intelligent fallback instead of manual fallback chain
  const url = getVariantUrl(photoObj, 'full') || photoObj.storageUrl || '';

  // Match pattern like /800/600 or /1600/1200
  const match = url.match(/\/(\d+)\/(\d+)/);
  if (match) {
    const width = parseInt(match[1], 10);
    const height = parseInt(match[2], 10);
    if (height > 0) {
      return width / height;
    }
  }

  // Fallback to default
  return DEFAULT_ASPECT_RATIO;
}

/**
 * HorizontalLayout component
 *
 * Renders photos in a horizontal filmstrip with:
 * - Container height determined by available space (ignores zoom)
 * - Uniform height for all photos (fills container height)
 * - Variable widths calculated by CSS aspect-ratio
 * - Horizontal scrolling (managed by parent scroll container)
 * - Aspect ratio preservation (no cropping or distortion)
 *
 * Uses pure CSS layout - no JavaScript height measurements or calculations.
 * Photo widths adapt naturally via CSS aspect-ratio property.
 *
 * @example
 * ```typescript
 * <HorizontalLayout
 *   items={photos}
 *   itemTemplate="photo-only"
 *   selectionMode="single"
 *   selectedIds={['photo-1']}
 *   onItemClick={handleClick}
 * />
 * ```
 */
export function HorizontalLayout({
  items,
  itemTemplate,
  selectionMode,
  selectedIds,
  zoom = 1.0, // Ignored for horizontal layout
  onItemClick,
  reorderContext,
  className,
}: PhotoCollection2LayoutProps) {
  // Refs for each photo container to enable scroll-into-view
  const photoRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Feature 081: photo-versioning - Get current user for ownership check
  const { user } = useAuth();
  const currentUserId = user?.id;

  // Scroll selected item into view when selection changes
  useEffect(() => {
    if (selectedIds.length > 0) {
      const selectedId = selectedIds[0]; // Scroll to first selected item
      const element = photoRefs.current.get(selectedId);
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      }
    }
  }, [selectedIds]);

  // Empty state
  if (items.length === 0) {
    return (
      <div className={cn('flex items-center justify-center h-64 text-center', className)}>
        <Text variant="muted">No photos in this collection</Text>
      </div>
    );
  }

  // Check if any items are selected (for disabling drag during selection)
  const hasSelection = selectedIds.length > 0;

  // Note: SortableContext is provided by PhotoCollection2, not here
  // This prevents double-wrapping which could cause drag-and-drop issues
  return (
    <div className={cn('flex flex-row gap-4 h-full', className)} >
      {items.map((photo) => {
        // Get aspect ratio for CSS aspect-ratio property
        const aspectRatio = getAspectRatio(photo);

        const setRef = (el: HTMLDivElement | null) => {
          if (el) {
            photoRefs.current.set(photo.photoData.id, el);
          } else {
            photoRefs.current.delete(photo.photoData.id);
          }
        };

        // Feature 081: photo-versioning - Check if current user owns this photo
        const isOwner = currentUserId ? photo.photoData.userId === currentUserId : false;

        return reorderContext?.enabled ? (
          <SortableHorizontalItem
            key={photo.photoData.id}
            photo={photo}
            aspectRatio={aspectRatio}
            itemTemplate={itemTemplate}
            selectionMode={selectionMode}
            isSelected={selectedIds.includes(photo.photoData.id)}
            zoom={zoom}
            onItemClick={onItemClick}
            reorderContext={reorderContext}
            hasSelection={hasSelection}
            setRef={setRef}
            currentUserId={currentUserId}
          />
        ) : (
          <div
            key={photo.photoData.id}
            ref={setRef}
            className="flex-shrink-0 h-full relative"
            style={{
              aspectRatio: aspectRatio.toString()
            }}
          >
            <PhotoContextMenu
              photoId={photo.photoData.id}
              isOwner={isOwner}
            >
              <PhotoItem2
                data={photo}
                template={itemTemplate}
                isSelected={selectionMode !== 'none' && selectedIds.includes(photo.photoData.id)}
                selectable={selectionMode !== 'none'}
                zoom={zoom}
                onClick={(e?: React.MouseEvent) => onItemClick?.(photo.photoData.id, e)}
              />
            </PhotoContextMenu>
          </div>
        );
      })}
    </div>
  );
}
